import React from "react";
import { useTranslation } from "react-i18next";

import Format from "../../../../helpers/Format";
import { ResponseProtest } from "../../../../services/ProtestoService";

import style from "./styles.module.scss";

const keyI18n = "translations.protestedTitles.result";

type ChipTone = "info" | "success";

interface ProtestResultProps {
  protest: ResponseProtest;
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className={style.field}>
      <span className={`pgm-title-medium ${style.fieldLabel}`}>{label}</span>
      <span className={`pgm-body-large ${style.fieldValue}`}>{value}</span>
    </div>
  );
}

function Chip({ children, tone }: { children: React.ReactNode; tone: ChipTone }) {
  return (
    <span className={`pgm-label-medium ${style.chip} ${style[tone]}`}>
      {children}
    </span>
  );
}

export default function ProtestResult({ protest }: ProtestResultProps) {
  const { t } = useTranslation();
  const { balance, code, contributor, inscription, requested_at } = protest;

  return (
    <section className={style.result}>
      <h2 className={`pgm-title-medium ${style.name}`}>
        {contributor.name} - {contributor.document}
      </h2>

      <div className={style.tags}>
        <Field label={t(`${keyI18n}.cda`)} value={inscription.cda_number} />

        {code && (
          <Chip tone="info">
            {t(`${keyI18n}.installment`, { code })}
          </Chip>
        )}

        <Chip tone="success">
          {t(`${keyI18n}.balance`, { value: Format.currency(balance ?? 0) })}
        </Chip>

        {requested_at && (
          <Chip tone="success">
            {t(`${keyI18n}.requestedAt`, {
              date: Format.date(requested_at, "long"),
            })}
          </Chip>
        )}
      </div>

      <div className={style.grid}>
        <Field label={t(`${keyI18n}.tribute`)} value={protest.receipt_type} />
        <Field
          label={t(`${keyI18n}.administrativeInscription`)}
          value={inscription.administrative_inscription}
        />
        <Field
          label={t(`${keyI18n}.inscribedAt`)}
          value={
            inscription.inscription_date
              ? Format.date(inscription.inscription_date, "long")
              : "-"
          }
        />
        <Field label={t(`${keyI18n}.fiscalYear`)} value={inscription.fiscal_date} />
        <Field
          label={t(`${keyI18n}.originalValue`)}
          value={Format.currency(inscription.original_value ?? 0)}
        />
        <Field label={t(`${keyI18n}.phase`)} value={inscription.state?.description} />
      </div>
    </section>
  );
}
